// EDEN · client-side calendar import parsing (Module B). Dependency-free.
//
// Turns a pasted grid, a .csv, or a native .xlsx into normalized rows
// { entry_date (ISO), label, type } for a calm preview before POST /api/calendar/import.
// The browser does the parsing (it has DecompressionStream + DOMParser); the API stays a
// thin validated bulk-insert. Team-tier, factual: dates + events only — no quality, no coaching.

export type EntryType = "teaching" | "test" | "event" | "holiday" | "training";
const CAL_TYPES: readonly EntryType[] = ["teaching", "test", "event", "holiday", "training"];

export interface ParsedRow {
  entry_date: string; // ISO YYYY-MM-DD when readable; otherwise the raw text (with `error` set)
  end_date?: string | null; // ISO, when the sheet carries a span column (ruling 6/8)
  label: string;
  type: EntryType;
  grades?: number[] | null; // parsed from "9 12" / "1-8 10-11" / "all" / blank (ruling 8)
  teaching_day?: 0 | 1; // parsed from yes/no/1/0/blank — undefined lets the server default by type
  note?: string;  // a calm aside (e.g. type defaulted) — not an error
  error?: string; // a friendly "needs a look" prompt — excluded from import
}

/** "9 12", "1-8 10-11", "all", blank → a sorted distinct grade list, or null for "every grade".
 *  Best-effort: any token that isn't a number or a M-N range is simply ignored, never a hard error
 *  — a scope column is a convenience, not a gate on the import. */
export function parseGrades(raw: string): number[] | null {
  const s = raw.trim().toLowerCase();
  if (!s || s === "all") return null;
  const set = new Set<number>();
  for (const tok of s.split(/[,\s]+/).filter(Boolean)) {
    const range = /^(\d{1,2})-(\d{1,2})$/.exec(tok);
    if (range) {
      const lo = Number(range[1]), hi = Number(range[2]);
      for (let g = lo; g <= hi; g++) if (g >= 1 && g <= 12) set.add(g);
    } else if (/^\d{1,2}$/.test(tok)) {
      const g = Number(tok);
      if (g >= 1 && g <= 12) set.add(g);
    }
  }
  return set.size ? [...set].sort((a, b) => a - b) : null;
}

/** yes/no/y/n/1/0/true/false, case-insensitive; blank → undefined (server defaults it by type). */
export function parseTeachingDay(raw: string): 0 | 1 | undefined {
  const s = raw.trim().toLowerCase();
  if (!s) return undefined;
  if (["yes", "y", "1", "true"].includes(s)) return 1;
  if (["no", "n", "0", "false"].includes(s)) return 0;
  return undefined;
}

// ── date + type normalization ──────────────────────────────────────────────────
const pad = (n: number): string => String(n).padStart(2, "0");

function isoFromParts(y: number, m: number, d: number): string | null {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

// Excel stores dates as a serial number of days since 1899-12-30 (the 1900 leap-year quirk).
function serialToIso(n: number): string {
  const d = new Date(Date.UTC(1899, 11, 30) + Math.round(n) * 86400000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
function monthName(raw: string): string | null {
  // "8 Jun 2026" / "8 June 2026"
  let m = /^(\d{1,2})\s+([A-Za-z]{3,})\.?,?\s+(\d{4})$/.exec(raw);
  if (m) {
    const mo = MONTHS.indexOf(m[2]!.slice(0, 3).toLowerCase());
    if (mo >= 0) return isoFromParts(+m[3]!, mo + 1, +m[1]!);
  }
  // "Jun 8, 2026" / "June 8 2026"
  m = /^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(raw);
  if (m) {
    const mo = MONTHS.indexOf(m[1]!.slice(0, 3).toLowerCase());
    if (mo >= 0) return isoFromParts(+m[3]!, mo + 1, +m[2]!);
  }
  return null;
}

/** Best-effort: ISO, YYYY/M/D, day-first D/M/YYYY, "D Mon YYYY", or an Excel date serial. */
export function toIso(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (n >= 20000 && n <= 80000) return serialToIso(n); // plausible Excel date serial (~1954–2119)
  }
  let m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s); // ISO / YYYY-M-D / YYYY/M/D
  if (m) return isoFromParts(+m[1]!, +m[2]!, +m[3]!);
  m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s); // D/M/YYYY (day-first — Cambodia/UK)
  if (m) return isoFromParts(+m[3]!, +m[2]!, +m[1]!);
  return monthName(s);
}

function normalizeType(raw: string): { type: EntryType; note?: string } {
  const t = raw.trim().toLowerCase();
  if (!t) return { type: "event" };
  if ((CAL_TYPES as readonly string[]).includes(t)) return { type: t as EntryType };
  return { type: "event", note: `Type “${raw.trim()}” → event` };
}

// ── rows → normalized ParsedRow[] (shared by the delimited + xlsx paths) ─────────
function rowsToParsed(rows: string[][]): ParsedRow[] {
  const nonEmpty = rows.filter((r) => r.some((c) => (c ?? "").trim() !== ""));
  if (nonEmpty.length === 0) return [];

  // Header detection: if the first row names date/label/type-ish columns, map by name. end_date /
  // grades / teaching_day (ruling 8) are ONLY recognized via a header — there's no positional
  // fallback for them (unlike date/label/type), since a headerless sheet has no way to say which
  // extra column means what.
  const head = (nonEmpty[0] ?? []).map((c) => (c ?? "").trim().toLowerCase());
  const find = (re: RegExp): number => head.findIndex((h) => re.test(h));
  const di = find(/date|day|when/);
  const li = find(/label|event|name|title|descr|detail/);
  const ti = find(/type|categ|kind/);
  const ei = find(/end.?date|until|to\b/);
  const gi = find(/grade/);
  const tdi = find(/teaching.?day/);
  // It's a header only if it names columns AND its date cell isn't itself a date — so a data
  // row whose type is literally "event" isn't mistaken for a header (`event` is in `li` above).
  const dateCol = di >= 0 ? di : 0;
  const hasHeader = (di >= 0 || li >= 0 || ti >= 0) && toIso((nonEmpty[0]?.[dateCol] ?? "").trim()) === null;
  const cols = hasHeader
    ? { date: di >= 0 ? di : 0, label: li >= 0 ? li : 1, type: ti >= 0 ? ti : 2, end: ei, grades: gi, teachingDay: tdi }
    : { date: 0, label: 1, type: 2, end: -1, grades: -1, teachingDay: -1 };

  const out: ParsedRow[] = [];
  for (let i = hasHeader ? 1 : 0; i < nonEmpty.length; i++) {
    const r = nonEmpty[i] ?? [];
    const rawDate = (r[cols.date] ?? "").trim();
    const label = (r[cols.label] ?? "").trim();
    const { type, note } = normalizeType(r[cols.type] ?? "");
    const rawEnd = cols.end >= 0 ? (r[cols.end] ?? "").trim() : "";
    const end_date = rawEnd ? toIso(rawEnd) : null;
    const grades = cols.grades >= 0 ? parseGrades(r[cols.grades] ?? "") : null;
    const teaching_day = cols.teachingDay >= 0 ? parseTeachingDay(r[cols.teachingDay] ?? "") : undefined;
    if (!rawDate && !label) continue;
    const iso = toIso(rawDate);
    if (!iso) { out.push({ entry_date: rawDate, label, type, note, error: "Couldn't read this date." }); continue; }
    if (!label) { out.push({ entry_date: iso, label, type, note, error: "This row has no label." }); continue; }
    out.push({ entry_date: iso, end_date, label, type, grades, teaching_day, note });
  }
  return out;
}

// ── delimited text (paste / .csv) ────────────────────────────────────────────────
function detectDelim(text: string): "\t" | "," {
  const firstLine = text.split(/\r\n|\r|\n/, 1)[0] ?? "";
  return firstLine.includes("\t") ? "\t" : ",";
}

/** RFC-ish tokenizer (handles quoted fields + embedded delimiters/newlines), parameterized by delimiter. */
function tokenize(text: string, delim: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charAt(i);
    if (inQuote) {
      if (ch === '"') {
        if (text.charAt(i + 1) === '"') { field += '"'; i++; } else inQuote = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuote = true;
    } else if (ch === delim) {
      row.push(field); field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text.charAt(i + 1) === "\n") i++;
      row.push(field); field = ""; rows.push(row); row = [];
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

export function parseDelimited(text: string): ParsedRow[] {
  return rowsToParsed(tokenize(text, detectDelim(text)));
}

// ── native .xlsx (a ZIP of XML) ──────────────────────────────────────────────────
async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  // Copy into a fresh ArrayBuffer-backed view (a subarray's buffer type isn't a valid BlobPart).
  const blob = new Blob([new Uint8Array(bytes)]);
  const stream = blob.stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

interface ZipEntry { method: number; data: Uint8Array }

function readZip(buf: ArrayBuffer): Map<string, ZipEntry> {
  const dv = new DataView(buf);
  const u8 = new Uint8Array(buf);
  const dec = new TextDecoder();

  // Locate the End Of Central Directory record (scan back from the tail).
  let eocd = -1;
  for (let i = u8.length - 22; i >= 0; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error("Not a valid .xlsx (no ZIP directory).");
  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);

  const entries = new Map<string, ZipEntry>();
  for (let n = 0; n < count; n++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break; // central-directory file header
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const fnLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOffset = dv.getUint32(p + 42, true);
    const name = dec.decode(u8.subarray(p + 46, p + 46 + fnLen));
    // The local header repeats fn/extra lengths; data starts after them.
    const lhFn = dv.getUint16(localOffset + 26, true);
    const lhExtra = dv.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + lhFn + lhExtra;
    entries.set(name, { method, data: u8.subarray(dataStart, dataStart + compSize) });
    p += 46 + fnLen + extraLen + commentLen;
  }
  return entries;
}

async function entryText(entries: Map<string, ZipEntry>, name: string): Promise<string | null> {
  const e = entries.get(name);
  if (!e) return null;
  const bytes = e.method === 0 ? e.data : await inflateRaw(e.data); // 0 = stored, 8 = deflate
  return new TextDecoder().decode(bytes);
}

function parseSharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  return Array.from(doc.getElementsByTagName("si")).map((si) =>
    Array.from(si.getElementsByTagName("t")).map((t) => t.textContent ?? "").join(""),
  );
}

function colIndex(ref: string): number {
  const letters = /^([A-Z]+)/.exec(ref)?.[1];
  if (!letters) return 0;
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

function parseSheet(xml: string, shared: string[]): string[][] {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const rows: string[][] = [];
  for (const row of Array.from(doc.getElementsByTagName("row"))) {
    const cells: string[] = [];
    for (const c of Array.from(row.getElementsByTagName("c"))) {
      const col = colIndex(c.getAttribute("r") ?? "");
      const t = c.getAttribute("t");
      if (t === "inlineStr") {
        cells[col] = Array.from(c.getElementsByTagName("t")).map((x) => x.textContent ?? "").join("");
      } else {
        const v = c.getElementsByTagName("v")[0]?.textContent ?? "";
        cells[col] = t === "s" ? (shared[Number(v)] ?? "") : v; // "s" → shared string; else number/str/serial
      }
    }
    rows.push(cells);
  }
  return rows;
}

export async function parseXlsx(buf: ArrayBuffer): Promise<ParsedRow[]> {
  const entries = readZip(buf);
  const shared = parseSharedStrings(await entryText(entries, "xl/sharedStrings.xml"));
  let sheetXml = await entryText(entries, "xl/worksheets/sheet1.xml");
  if (!sheetXml) {
    const firstSheet = [...entries.keys()].find((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k));
    if (firstSheet) sheetXml = await entryText(entries, firstSheet);
  }
  if (!sheetXml) throw new Error("No worksheet found in the .xlsx.");
  return rowsToParsed(parseSheet(sheetXml, shared));
}
